import { Component } from '@theme/component';

/**
 * ms-reviews-component — Carrusel ligero (scroll-snap) para el riel de reseñas
 * de la sección ms-reviews (spec H10 / P9).
 *
 * Igual filosofía que ms-related-component: NO maneja datos de reseñas (esos son
 * blocks manuales o, en el futuro, metafields de una app). Solo gobierna la
 * navegación del riel en mobile: dots + flechas + swipe, con a11y completa
 * (aria-current en dots, disabled en flechas, teclado izq/der en la tira de dots).
 * En desktop el riel es un grid y este componente se desactiva solo (data-no-overflow).
 *
 * Refs:
 *   track       - el riel scrolleable (overflow-x:auto, scroll-snap).
 *   dot[]       - botones de paginación (cota superior = 1 por card).
 *   prev / next - flechas (opcionales).
 *
 * Atributos:
 *   data-loop="true" - permite saltar de la última página a la primera y viceversa.
 */
class MsReviewsComponent extends Component {
  requiredRefs = ['track'];

  #abortController = new AbortController();

  /** @type {number} Página activa (índice basado en 0). */
  #activeIndex = 0;

  /** @type {number} Total de páginas calculadas a partir del scroll. */
  #pageCount = 1;

  /** @type {number | undefined} */
  #scrollRaf;

  /** @type {ResizeObserver | undefined} */
  #resizeObserver;

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#abortController;
    const { track } = this.refs;
    if (!(track instanceof HTMLElement)) return;

    // Sincronizar dots con la posición real al hacer scroll/swipe.
    track.addEventListener('scroll', this.#onScroll, { passive: true, signal });

    // Recalcular páginas cuando cambia el tamaño (rotación, breakpoint mobile↔desktop).
    if ('ResizeObserver' in window) {
      this.#resizeObserver = new ResizeObserver(() => this.#measure());
      this.#resizeObserver.observe(track);
    } else {
      window.addEventListener('resize', this.#measure, { signal });
    }

    this.#measure();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
    this.#resizeObserver?.disconnect();
    if (this.#scrollRaf) cancelAnimationFrame(this.#scrollRaf);
  }

  /** Re-medir tras un re-render del Section Rendering API (editor / morph). */
  updatedCallback() {
    super.updatedCallback?.();
    this.#measure();
  }

  /** Calcula cuántas "páginas" hay (ancho total / ancho visible) y sincroniza UI. */
  #measure = () => {
    const { track } = this.refs;
    if (!(track instanceof HTMLElement)) return;

    const visible = track.clientWidth;
    const total = track.scrollWidth;
    const pages = visible > 0 ? Math.max(1, Math.round(total / visible)) : 1;
    this.#pageCount = pages;

    // Sin overflow (desktop grid o pocas cards) → ocultar controles vía CSS.
    this.toggleAttribute('data-no-overflow', pages <= 1);

    this.#syncActiveFromScroll();
    this.#renderControls();
  };

  #onScroll = () => {
    if (this.#scrollRaf) cancelAnimationFrame(this.#scrollRaf);
    this.#scrollRaf = requestAnimationFrame(() => {
      this.#syncActiveFromScroll();
      this.#renderControls();
    });
  };

  /** Deriva la página activa de la posición de scroll. */
  #syncActiveFromScroll() {
    const { track } = this.refs;
    if (!(track instanceof HTMLElement)) return;

    const visible = track.clientWidth;
    if (visible <= 0) return;

    const max = track.scrollWidth - visible;
    const left = track.scrollLeft;
    let index = Math.round(left / visible);

    if (left >= max - 1) index = this.#pageCount - 1;
    this.#activeIndex = Math.min(Math.max(index, 0), this.#pageCount - 1);
  }

  /** Actualiza dots (aria-current/visibilidad) y estado de flechas. */
  #renderControls() {
    const dots = this.#dots;
    dots.forEach((dot, i) => {
      const within = i < this.#pageCount;
      dot.hidden = !within;
      if (!within) {
        dot.setAttribute('aria-current', 'false');
        dot.classList.remove('is-active');
        dot.tabIndex = -1;
        return;
      }
      const isActive = i === this.#activeIndex;
      dot.setAttribute('aria-current', isActive ? 'true' : 'false');
      dot.classList.toggle('is-active', isActive);
      dot.tabIndex = isActive ? 0 : -1;
    });

    const loop = this.dataset.loop === 'true';
    const { prev, next } = this.refs;
    if (prev instanceof HTMLButtonElement) {
      prev.disabled = !loop && this.#activeIndex <= 0;
    }
    if (next instanceof HTMLButtonElement) {
      next.disabled = !loop && this.#activeIndex >= this.#pageCount - 1;
    }
  }

  /** @returns {HTMLButtonElement[]} */
  get #dots() {
    const { dot } = this.refs;
    if (!dot) return [];
    return /** @type {HTMLButtonElement[]} */ (Array.isArray(dot) ? dot : [dot]);
  }

  /**
   * Desplaza a una página por índice. Lo invoca un dot vía on:click="/goTo?index=".
   * @param {{ index: number | string }} data
   */
  goTo(data) {
    const index = typeof data === 'object' ? Number(data.index) : Number(data);
    if (Number.isNaN(index)) return;
    this.#scrollToPage(index);
  }

  /** Página anterior (flecha). Llamado vía on:click="/prevPage". */
  prevPage() {
    let target = this.#activeIndex - 1;
    if (target < 0) target = this.dataset.loop === 'true' ? this.#pageCount - 1 : 0;
    this.#scrollToPage(target);
  }

  /** Página siguiente (flecha). Llamado vía on:click="/nextPage". */
  nextPage() {
    let target = this.#activeIndex + 1;
    if (target > this.#pageCount - 1) target = this.dataset.loop === 'true' ? 0 : this.#pageCount - 1;
    this.#scrollToPage(target);
  }

  /**
   * Teclado en la tira de dots: flechas izq/der mueven foco + página.
   * Llamado vía on:keydown="/onDotKeydown".
   * @param {KeyboardEvent} event
   */
  onDotKeydown(event) {
    const key = event.key;
    if (key !== 'ArrowLeft' && key !== 'ArrowRight') return;
    event.preventDefault();
    const delta = key === 'ArrowRight' ? 1 : -1;
    let target = this.#activeIndex + delta;
    target = Math.min(Math.max(target, 0), this.#pageCount - 1);
    this.#scrollToPage(target);
    this.#dots[target]?.focus();
  }

  /**
   * @param {number} index
   */
  #scrollToPage(index) {
    const { track } = this.refs;
    if (!(track instanceof HTMLElement)) return;

    const clamped = Math.min(Math.max(index, 0), this.#pageCount - 1);
    const left = track.clientWidth * clamped;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    track.scrollTo({ left, behavior: reduce ? 'auto' : 'smooth' });
    this.#activeIndex = clamped;
    this.#renderControls();
  }
}

if (!customElements.get('ms-reviews-component')) {
  customElements.define('ms-reviews-component', MsReviewsComponent);
}
